/* istanbul ignore file */
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Pagination } from 'semantic-ui-react'
import _clamp from 'lodash/clamp'
import useUpdateEffect from '../useUpdateEffect/'

const DEFAULT_PAGE_SIZE = 20
const FIRST_PAGE = 1

const getStartIdx = (pageSize, pageNumber, total) =>
  Math.min(pageSize * (pageNumber - 1) + 1, total)

const getEndIdx = (startIdx, pageSize, total) =>
  Math.min(startIdx + pageSize - 1, total)

const getOffset = (activePage, pageSize = DEFAULT_PAGE_SIZE) =>
  (activePage - 1) * pageSize

const calculatePageNumber = (offset = 0, pageSize = DEFAULT_PAGE_SIZE) =>
  pageSize !== 0 ? Math.floor(offset / pageSize) + 1 : 1

const calculateTotalPages = (total = 0, pageSize = DEFAULT_PAGE_SIZE) =>
  pageSize !== 0 ? Math.ceil(total / pageSize) : 0

const noop = () => {}

const useOffsetPagination = ({
  totalCount,
  initialOffset = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  elmId = '',
  onPageChange = noop,
}) => {
  const [offset, setOffset] = useState(initialOffset)
  const [activePage, setActivePage] = useState(
    calculatePageNumber(offset, pageSize)
  )
  const paginationState = useMemo(() => {
    const totalPages = calculateTotalPages(totalCount, pageSize)
    const startIdx = getStartIdx(pageSize, activePage, totalCount)

    return {
      currentPage: activePage,
      totalPages,
      startIdx,
      pageSize,
      disablePrevious: activePage === FIRST_PAGE,
      disableNext: activePage >= totalPages, // when there's no results, totalPages can be 0 but the first activePage is 1 so doing activePage greater than or equal to totalPages avoids a bug where you can click next with zero results
      endIdx: getEndIdx(startIdx, pageSize, totalCount),
      offset: getOffset(activePage, pageSize),
    }
  }, [totalCount, pageSize, activePage])

  // I don't like useUpdateEffect since linter can't statically check
  // the dependency array
  useEffect(() => {
    setOffset(initialOffset)
    setActivePage(calculatePageNumber(initialOffset, pageSize))
  }, [initialOffset, pageSize])

  useUpdateEffect(() => {
    onPageChange(paginationState)
  }, [paginationState])

  const PageSelection = useMemo(() => {
    return (
      <Pagination
        data-elm-id={`${elmId}-page-selection`}
        activePage={paginationState.currentPage}
        totalPages={paginationState.totalPages}
        firstItem={false}
        lastItem={false}
        pageItem={{ 'data-elm-id': 'number_pagination' }}
        prevItem={{
          content: '\u27E8',
          disabled: paginationState.disablePrevious,
          'data-elm-id': 'prev_pagination',
        }}
        nextItem={{
          content: '\u27E9',
          disabled: paginationState.disableNext,
          'data-elm-id': 'next_pagination',
        }}
        onPageChange={(e, { activePage }) => setActivePage(activePage)}
      />
    )
  }, [paginationState, elmId])

  const PageIndicator = useMemo(() => {
    return (
      <div data-elm-id={`${elmId}-page-indicator`}>
        Showing {paginationState.startIdx} - {paginationState.endIdx} of{' '}
        {totalCount} Results
      </div>
    )
  }, [paginationState, totalCount, elmId])

  const Paginator = useMemo(() => {
    return (
      <nav
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
        }}
        data-elm-id={`${elmId}-paginator`}
      >
        {PageSelection}
        {PageIndicator}
      </nav>
    )
  }, [PageSelection, PageIndicator, elmId])

  const updatePagination = useCallback(
    targetPage => {
      const totalPages = calculateTotalPages(totalCount, pageSize)
      const newActivePage = _clamp(targetPage, FIRST_PAGE, totalPages)
      setActivePage(newActivePage)
    },
    [setActivePage, totalCount, pageSize]
  )

  return {
    PageSelection, // deprecated, use Paginator instead
    PageIndicator, // deprecated, use Paginator instead
    Paginator,
    updatePagination,
    getOffset,
  }
}

export default useOffsetPagination
